import { TO_DO_STATUS } from 'enums';
import { MOBILE_APP_EVENT } from 'constants/events';
import {
  EISENHOWER_QUADRANT,
  LOCAL_STORAGE,
  MAC_APP_DATA_LOCATION,
  PLATFORMS
} from 'constants/general';
import {
  SendDataToPlatformPayload,
  SyncWithDevicesArgs
} from 'interfaces/bridge.interface';
import { handleNativeTokenRefreshResponse } from 'bridge/native-auth.bridge';
import {
  postToMacWebkitHandler,
  postToReactNativeWebView,
  postToWindowsWebView
} from 'bridge/platform-messaging.bridge';
import {
  addFlag,
  updateFlags,
  updateIsSettingEditingFinished,
  updateIsUserSettingsModified,
  updatePreviousSettings,
  updateThemeMode
} from 'store/reducer/setting/slice';
import {
  setPlayerIsSyncingFromDesktop,
  syncWithDevicesPlayerTasks,
  updateFocusPlayerTotalDuration,
  updateIsPausedFromDesktop,
  updateBrainDump,
  updatePlayerFocusAlignmentScore,
  updatePlayerIntention,
  updatePlayerIsReadyToSendUpdates,
  updatePlayerSelectedTask,
  updatePlayerTasks
} from 'store/reducer/to-do/slice';
import { getAppDispatch } from 'store/store-access';
import {
  formatToDoPlayerTaskTimeConstraints,
  safeStringify
} from 'utils/helpers';
import { captureException } from 'services/posthog.service';
import { sanitizeInput } from 'utils/todo.util';
import { parseIncomingTasks } from 'bridge/parse-incoming-tasks.util';
import { getLocalStorage } from 'utils/helpers/storage';
import { SettingsType } from 'interfaces/setting.interface';

export const exposeRefreshWebview = () => {
  window.refreshWebview = () => {
    window.location.reload();
  };
};

export const exposeSyncWithDevices = () =>
  (window.syncWithDevices = (args: SyncWithDevicesArgs) => {
    if (args.accessToken) {
      void handleNativeTokenRefreshResponse(args.accessToken, args.requestId);
    }

    const {
      totalDuration,
      theme,
      intention,
      brainDump,
      todoPlayer,
      flags,
      flag
    } = args;

    const actions = [];
    const hasIncomingNewTasks = Boolean(todoPlayer?.newTasks?.length);

    getAppDispatch()?.(setPlayerIsSyncingFromDesktop(true));

    try {
      if (totalDuration) {
        actions.push(updateFocusPlayerTotalDuration(totalDuration));
      }

      if (theme) {
        actions.push(updateThemeMode(theme));
      }

      if (intention) {
        actions.push(updatePlayerIntention(intention));
      }

      if (brainDump) {
        const sanitizedBrainDump = sanitizeInput(brainDump);
        actions.push(updateBrainDump(sanitizedBrainDump));
      }

      if (todoPlayer?.isPaused !== undefined) {
        actions.push(updateIsPausedFromDesktop(todoPlayer.isPaused));
      }

      if (todoPlayer?.selectedTask) {
        const {
          markCurrentTaskAsComplete,
          skipAddingToList,
          ...incomingSelectedTask
        } = todoPlayer.selectedTask;
        const isMarkCurrentTaskAsComplete =
          markCurrentTaskAsComplete === 1 || markCurrentTaskAsComplete === true;

        const currentPlayingTasks = parseIncomingTasks({
          tasks: getLocalStorage(LOCAL_STORAGE.TO_DO_PLAYER_TASKS)
        });
        const currentPlayingTask = currentPlayingTasks.find(
          (task) => !!task.isPlaying
        );

        let tasksToUse = currentPlayingTasks;

        if (isMarkCurrentTaskAsComplete && currentPlayingTask) {
          tasksToUse = tasksToUse.map((task) =>
            task.id === currentPlayingTask?.id && task.isPlaying
              ? { ...task, isPlaying: false, status: TO_DO_STATUS.COMPLETED }
              : task
          );
        }

        let taskToPlay = incomingSelectedTask;

        const existingTask = tasksToUse.find(
          (task) => task.id === incomingSelectedTask.id
        );

        if (existingTask) {
          taskToPlay = {
            ...existingTask,
            status: TO_DO_STATUS.IN_PROGRESS,
            isPlaying: true,
            focusedDuration: 0
          };
        } else if (!skipAddingToList) {
          // Only add the task to the list when it doesn't already exist AND the caller
          // hasn't explicitly requested that we skip the insertion (e.g. when the Mac app
          // calls playExistingTaskInCurrentList for a task that is already in Core Data
          // but may not be present in the current web-view state).
          taskToPlay = {
            ...incomingSelectedTask,
            status: TO_DO_STATUS.IN_PROGRESS,
            eisenhower_quadrant: parseInt(EISENHOWER_QUADRANT.DO),
            startTime: '',
            endTime: '',
            focusedDuration: 0,
            isPlaying: true
          };
          tasksToUse = [taskToPlay, ...tasksToUse];
        } else {
          // skipAddingToList is true but the task wasn't found in the current list.
          // This can happen if the web view was reloaded after the task was originally
          // added. Play it in-place without re-inserting to avoid duplicates.
          taskToPlay = {
            ...incomingSelectedTask,
            status: TO_DO_STATUS.IN_PROGRESS,
            eisenhower_quadrant: parseInt(EISENHOWER_QUADRANT.DO),
            startTime: '',
            endTime: '',
            focusedDuration: 0,
            isPlaying: true
          };
        }

        actions.push(
          updatePlayerSelectedTask(taskToPlay),
          updatePlayerTasks(formatToDoPlayerTaskTimeConstraints(tasksToUse))
        );
      }

      if (todoPlayer?.newTasks?.length) {
        const formattedNewTasks = todoPlayer.newTasks.map((task) => ({
          ...task,
          eisenhower_quadrant: parseInt(EISENHOWER_QUADRANT.DO),
          status: TO_DO_STATUS.NOT_STARTED,
          startTime: '',
          endTime: '',
          focusedDuration: 0,
          isPlaying: false
        }));
        actions.push(syncWithDevicesPlayerTasks(formattedNewTasks));
      }

      if (flags?.length) {
        actions.push(updateFlags(flags));
      }

      if (flag) {
        actions.push(addFlag(flag));
      }

      if (todoPlayer?.focusAlignmentScore !== undefined) {
        actions.push(
          updatePlayerFocusAlignmentScore(todoPlayer.focusAlignmentScore)
        );
      }

      actions.forEach((action) => getAppDispatch()?.(action));
    } finally {
      getAppDispatch()?.(setPlayerIsSyncingFromDesktop(false));
      if (hasIncomingNewTasks) {
        // Done: PR-3082 but still desktop apps are not updated yet.
        // Temporary backward-compatibility shim for older desktop releases:
        // some clients rely on receiving the updated tasks payload back after sending
        // todoPlayer.newTasks. Trigger one outbound sync after the inbound sync lock
        // is released to avoid reintroducing echo loops for all player updates.
        getAppDispatch()?.(updatePlayerIsReadyToSendUpdates(true));
      }
    }
  });

export const sendDataToPlatform = (
  platformType: string,
  data: SendDataToPlatformPayload,
  mac_where?: string,
  mobile_where?: string
) => {
  try {
    if (platformType === PLATFORMS.MAC) {
      handleMacAppEvent(data, mac_where);
    }

    if (
      [PLATFORMS.MOB, PLATFORMS.ANDROID, PLATFORMS.IOS].includes(platformType)
    ) {
      handleMobileAppEvent(data, mobile_where);
    }

    platformType === PLATFORMS.WIN && postToWindowsWebView(data);
  } catch (error) {
    captureException(error);
  }
};

const handleMacAppEvent = (
  data: SendDataToPlatformPayload,
  mac_where?: string
) => {
  const stringifiedData = safeStringify(data).trim();
  switch (mac_where) {
    case MAC_APP_DATA_LOCATION.CLOSE_SURVEY_UI:
      postToMacWebkitHandler('close_survey_ui', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.CLOSE_TODO_TASK_UI:
      postToMacWebkitHandler('close_todo_task_ui', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.START_FOCUS_MODE:
      postToMacWebkitHandler('get_selected_tasks', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.END_FOCUS_MODE:
      postToMacWebkitHandler('submit_completed_tasks', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.USER_SETTINGS_UPDATED:
      postToMacWebkitHandler('detect_user_settings_change', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.SEND_NEW_TASK_FROM_TO_DO_PLAYER:
      postToMacWebkitHandler(
        'send_new_task_added_to_todo_player',
        stringifiedData
      );
      break;
    case MAC_APP_DATA_LOCATION.SEND_SUGGESTED_ACTIVITIES:
      postToMacWebkitHandler('send_suggested_activities', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.SEND_SELECTED_HABIT_PACK_ID:
      postToMacWebkitHandler('send_installed_habit_pack_id', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.SEND_USER_SUBSCRIPTION_STATUS:
      postToMacWebkitHandler('send_user_subscription_status', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.ROUTINE_SUGGESTIONS_STEP:
      postToMacWebkitHandler('routine_suggestions_step', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.SEND_INTENTION:
      postToMacWebkitHandler('send_intention', stringifiedData);
      break;
    case MAC_APP_DATA_LOCATION.REQUEST_ACCESS_TOKEN:
      postToMacWebkitHandler('request_access_token', stringifiedData);
      break;
    default:
      postToMacWebkitHandler('setting_saved', data);
  }
};

const handleMobileAppEvent = (
  data: SendDataToPlatformPayload,
  mobile_where?: string
) => {
  const eventsWithData = new Set([
    MOBILE_APP_EVENT.SUGGESTED_ACTIVITIES_INSTALLED,
    MOBILE_APP_EVENT.SEND_SELECTED_HABIT_PACK_ID,
    MOBILE_APP_EVENT.EDIT_HABITS_ENABLED,
    MOBILE_APP_EVENT.SAVED_USER_SETTINGS
  ]);

  if (eventsWithData?.has(mobile_where ?? '')) {
    postToReactNativeWebView(JSON.stringify({ event: mobile_where, data }));
  } else if (mobile_where === MOBILE_APP_EVENT.CLOSE_SURVEY_UI) {
    postToReactNativeWebView(safeStringify(data));
  } else {
    [
      updateIsSettingEditingFinished(false),
      updatePreviousSettings(data as SettingsType),
      updateIsUserSettingsModified(false)
    ].forEach((action) => getAppDispatch()?.(action));

    postToReactNativeWebView(safeStringify(data));
  }
};
